import { WARNING_SESSION_COUNT } from "@modules/boxConsumable/entities/boxConsumableConst"
import { BoxConsumableStatus } from "@modules/boxConsumable/entities/boxConsumableType"
import { BoxConsumableDetailsDto } from "@modules/boxConsumable/gateway/boxConsumableApiDto"
import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType, Price } from "@modules/boxOffer/entities/boxOfferTypes"
import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { Language } from "@modules/language/utils/languageUtils"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"
import { DefaultTheme } from "styled-components"

type BoxConsumableDetailsState = Omit<
  BoxConsumableDetailsEntity,
  | "getWordingStatus"
  | "getColorStatus"
  | "getWordingCouponDate"
  | "getWordingCouponPriceAfterDiscount"
  | "isDisplayWarning"
  | "getWordingWarning"
>
export class BoxConsumableDetailsEntity {
  id: string
  type: BoxOfferType
  creationDate: Date
  endValidity: Date | null
  status: BoxConsumableStatus
  count: number | null
  referencePrice: Price | null
  priceObject: Price | null
  usedPromoCode: LightPromoCodeEntity | null
  coupon: CouponEntity | null
  offer: BoxOfferDetailedEntity

  private constructor(boxConsumableDetails: BoxConsumableDetailsState) {
    this.id = boxConsumableDetails.id
    this.type = boxConsumableDetails.type
    this.creationDate = boxConsumableDetails.creationDate
    this.endValidity = boxConsumableDetails.endValidity
    this.status = boxConsumableDetails.status
    this.count = boxConsumableDetails.count
    this.referencePrice = boxConsumableDetails.referencePrice
    this.priceObject = boxConsumableDetails.priceObject
    this.usedPromoCode = boxConsumableDetails.usedPromoCode
    this.coupon = boxConsumableDetails.coupon
    this.offer = boxConsumableDetails.offer
  }

  static fromDto(boxConsumableDetailsDto: BoxConsumableDetailsDto): BoxConsumableDetailsEntity {
    return new BoxConsumableDetailsEntity({
      id: boxConsumableDetailsDto.id,
      type: boxConsumableDetailsDto.type,
      creationDate: new Date(boxConsumableDetailsDto.creationDate),
      endValidity: boxConsumableDetailsDto.endValidity ? new Date(boxConsumableDetailsDto.endValidity) : null,
      status: boxConsumableDetailsDto.status,
      count: boxConsumableDetailsDto.count,
      referencePrice: boxConsumableDetailsDto.referencePrice,
      priceObject: boxConsumableDetailsDto.priceObject,
      usedPromoCode: boxConsumableDetailsDto.usedPromoCode ? LightPromoCodeEntity.fromDto(boxConsumableDetailsDto.usedPromoCode) : null,
      coupon: boxConsumableDetailsDto.coupon ? CouponEntity.fromDto(boxConsumableDetailsDto.coupon) : null,
      offer: BoxOfferDetailedEntity.fromDto(boxConsumableDetailsDto.offer),
    })
  }

  getWordingStatus(): string {
    switch (this.status) {
      case BoxConsumableStatus.PAID:
        if (this.count === 0 && this.offer.type === BoxOfferType.TicketsBook)
          return word("box.members.tab.offer.status.expiredPass")
        else if (this.count === 0) return word("box.members.tab.offer.status.expired")
        return word("box.members.tab.offer.status.ongoing")
      case BoxConsumableStatus.PENDING_PAYMENT:
        return ""
      default:
        return ""
    }
  }

  getColorStatus(theme: DefaultTheme): string {
    switch (this.status) {
      case BoxConsumableStatus.PAID:
        if (this.count === 0) return theme.error
        return theme.sucess
      case BoxConsumableStatus.PENDING_PAYMENT:
        return theme.warning
      default:
        return theme.error
    }
  }

  getWordingCouponDate(): string {
    return ""
  }

  // The coupon is calculated after calculating the promo code if there is one
  getWordingCouponPriceAfterDiscount(language: Language): string {
    if (!this.priceObject || !this.referencePrice || !this.coupon) {
      return ""
    }

    let price = this.referencePrice.value
    if (this.usedPromoCode !== null) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }

    price =
      this.coupon.type === CouponType.PERCENT ? price - (price * this.coupon.value) / 100 : price - this.coupon.value

    return formatCurrency(price, this.priceObject.currency, language)
  }

  isDisplayWarning(): boolean {
    if (
      this.status === BoxConsumableStatus.PAID &&
      this.offer.type === BoxOfferType.TicketsBook &&
      this.count !== null &&
      this.count > 0 &&
      this.count <= WARNING_SESSION_COUNT
    ) {
      return true
    }
    return false
  }

  getWordingWarning(): string {
    if (this.count === null) return ""
    return word("box.purchases.list.item.endCount", {
      count: this.count ?? 0,
    })
  }
}
